import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { GridSystem } from '@gameplay/building/GridSystem';
import { RoomDefinition } from '@gameplay/management/rooms/RoomTypes';

export class RoomQualityOverlay extends Container {
  private readonly gridSystem: GridSystem;
  private qualityIndicators: Map<string, Graphics> = new Map();
  private qualityTexts: Map<string, Text> = new Map();
  private isVisible: boolean = false;

  constructor(gridSystem: GridSystem) {
    super();
    this.gridSystem = gridSystem;
    this.visible = false;
  }

  public updateRoom(room: RoomDefinition): void {
    // Remove old indicators for this room
    room.tiles.forEach(tile => {
      const key = `${tile.gridX}_${tile.gridY}_${tile.floor}`;
      this.removeIndicator(key);
    });

    // Add new quality indicators
    if (this.isVisible) {
      this.createRoomQualityIndicators(room);
    }
  }

  public removeRoom(roomId: string, tiles: Array<{gridX: number, gridY: number, floor: number}>): void {
    tiles.forEach(tile => {
      const key = `${tile.gridX}_${tile.gridY}_${tile.floor}`;
      this.removeIndicator(key);
    });
  }

  private removeIndicator(key: string): void {
    const indicator = this.qualityIndicators.get(key);
    if (indicator) {
      this.removeChild(indicator);
      indicator.destroy();
      this.qualityIndicators.delete(key);
    }

    const text = this.qualityTexts.get(key);
    if (text) {
      this.removeChild(text);
      text.destroy();
      this.qualityTexts.delete(key);
    }
  }

  private createRoomQualityIndicators(room: RoomDefinition): void {
    const qualityColor = this.getQualityColor(room.quality);
    const alpha = 0.6;

    room.tiles.forEach(tile => {
      const worldPos = this.gridSystem.gridToWorld(tile.gridX, tile.gridY);
      const key = `${tile.gridX}_${tile.gridY}_${tile.floor}`;

      // Create quality indicator overlay
      const indicator = new Graphics();
      indicator.beginFill(qualityColor, alpha);
      indicator.drawPolygon([
        0, GridSystem.TILE_HEIGHT / 2,
        GridSystem.TILE_WIDTH / 2, 0,
        GridSystem.TILE_WIDTH, GridSystem.TILE_HEIGHT / 2,
        GridSystem.TILE_WIDTH / 2, GridSystem.TILE_HEIGHT
      ]);
      indicator.endFill();

      // Position the overlay
      indicator.position.set(worldPos.x - GridSystem.TILE_WIDTH / 2, worldPos.y - GridSystem.TILE_HEIGHT / 2);
      this.addChild(indicator);
      this.qualityIndicators.set(key, indicator);

      // Add quality score text (only on center tile for multi-tile rooms)
      if (this.isCenterTile(tile, room)) {
        const qualityText = new Text(
          `${Math.round(room.quality)}%`,
          new TextStyle({
            fontFamily: 'Arial',
            fontSize: 12,
            fill: 0xffffff,
            stroke: '#000000',
            strokeThickness: 2,
            align: 'center'
          })
        );

        qualityText.anchor.set(0.5, 0.5);
        qualityText.position.set(worldPos.x, worldPos.y - GridSystem.TILE_HEIGHT / 4);
        this.addChild(qualityText);
        this.qualityTexts.set(key, qualityText);
      }
    });
  }

  private isCenterTile(tile: any, room: RoomDefinition): boolean {
    if (room.tiles.length === 1) return true;

    // Find center of room
    const avgX = room.tiles.reduce((sum, t) => sum + t.gridX, 0) / room.tiles.length;
    const avgY = room.tiles.reduce((sum, t) => sum + t.gridY, 0) / room.tiles.length;

    // Find tile closest to center
    const distances = room.tiles.map(t => 
      Math.abs(t.gridX - avgX) + Math.abs(t.gridY - avgY)
    );
    const minDistance = Math.min(...distances);
    const centerIndex = distances.indexOf(minDistance);

    return room.tiles[centerIndex] === tile;
  }

  private getQualityColor(quality: number): number {
    // Color gradient from red (poor) to green (excellent)
    if (quality >= 80) return 0x4CAF50; // Green - Excellent
    if (quality >= 60) return 0x8BC34A; // Light Green - Good
    if (quality >= 40) return 0xFFEB3B; // Yellow - Fair
    if (quality >= 20) return 0xFF9800; // Orange - Poor
    return 0xF44336; // Red - Very Poor
  }

  public showOverlay(): void {
    this.isVisible = true;
    this.visible = true;
  }

  public hideOverlay(): void {
    this.isVisible = false;
    this.visible = false;
    this.clearAllIndicators();
  }

  public toggleOverlay(): void {
    if (this.isVisible) {
      this.hideOverlay();
    } else {
      this.showOverlay();
    }
  }

  private clearAllIndicators(): void {
    this.qualityIndicators.forEach(indicator => {
      this.removeChild(indicator);
      indicator.destroy();
    });
    this.qualityIndicators.clear();

    this.qualityTexts.forEach(text => {
      this.removeChild(text);
      text.destroy();
    });
    this.qualityTexts.clear();
  }

  public refreshAllRooms(rooms: RoomDefinition[]): void {
    this.clearAllIndicators();
    if (this.isVisible) {
      rooms.forEach(room => this.createRoomQualityIndicators(room));
    }
  }
}